import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabaseClient'
import { decryptField as decrypt } from '../lib/secureFields'
import { useCurrency } from '../hooks/useCurrency'
import { useLanguage } from '../hooks/useLanguage'

// ─── Burbuja de mensaje ──────────────────────────────────────────────────────
function MessageBubble({ msg, t }) {
  const isUser = msg.role === 'user'
  const isLoading = msg.loading

  return (
    <div style={{
      display: 'flex',
      justifyContent: isUser ? 'flex-end' : 'flex-start',
      marginBottom: 14,
      animation: 'bubbleIn 0.28s cubic-bezier(0.34,1.56,0.64,1)',
    }}>
      {!isUser && (
        <div style={{
          width: 34, height: 34, borderRadius: '50%',
          background: 'linear-gradient(135deg,#22c55e,#16a34a)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 16, flexShrink: 0, marginRight: 10, marginTop: 2,
          boxShadow: '0 2px 8px rgba(34,197,94,0.35)',
        }}>🤖</div>
      )}
      <div style={{
        maxWidth: '78%',
        padding: isLoading ? '14px 18px' : '12px 16px',
        borderRadius: isUser
          ? '18px 18px 4px 18px'
          : '18px 18px 18px 4px',
        background: isUser
          ? 'linear-gradient(135deg,#22c55e,#16a34a)'
          : 'var(--color-surface)',
        color: isUser ? 'white' : 'var(--color-text)',
        fontSize: 14,
        lineHeight: 1.6,
        border: isUser ? 'none' : '1px solid var(--color-border)',
        boxShadow: isUser
          ? '0 4px 16px rgba(34,197,94,0.25)'
          : '0 2px 8px rgba(0,0,0,0.15)',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}>
        {isLoading ? (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: 'var(--color-muted)' }}>{t('ia_analizando')}</span>
            <div style={{ display: 'flex', gap: 4 }}>
              {[0,1,2].map(i => (
                <div key={i} style={{
                  width: 6, height: 6, borderRadius: '50%',
                  background: '#22c55e',
                  animation: `dotBounce 0.6s infinite ${i * 0.15}s`,
                }}/>
              ))}
            </div>
          </div>
        ) : msg.content}
      </div>
    </div>
  )
}

// ─── Componente principal ────────────────────────────────────────────────────
export default function IA() {
  const navigate = useNavigate()
  const { format } = useCurrency()
  const { t } = useLanguage()

  const QUICK_SUGGESTIONS = [
    t('ia_sugerencia_1'),
    t('ia_sugerencia_2'),
    t('ia_sugerencia_3'),
    t('ia_sugerencia_4'),
    t('ia_sugerencia_5'),
    t('ia_sugerencia_6'),
  ]

  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [financialContext, setFinancialContext] = useState('')
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(true)
  const [isOnline, setIsOnline] = useState(navigator.onLine)

  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)

  // ── Detección de conexión a internet ─────────────────────────────────────
  useEffect(() => {
    function handleOnline() { setIsOnline(true) }
    function handleOffline() { setIsOnline(false) }
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  // ── Scroll al último mensaje ───────────────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // ── Cargar datos del usuario ───────────────────────────────────────────────
  useEffect(() => {
    if (!isOnline) { setLoading(false); return }

    async function loadUserData() {
      try {
        const { data, error } = await supabase.auth.getUser()
        if (error || !data?.user) { navigate('/login'); return }
        const currentUser = data.user
        setUser(currentUser)

        const name = currentUser.user_metadata?.display_name
          || currentUser.user_metadata?.full_name?.split(' ')[0]
          || t('usuario_generico')

        // Movimientos de los últimos 3 meses
        const threeMonthsAgo = new Date()
        threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3)
        const { data: movimientos } = await supabase
          .from('transactions')
          .select('amount, type, date, category, description')
          .eq('user_id', currentUser.id)
          .gte('date', threeMonthsAgo.toISOString().split('T')[0])
          .order('date', { ascending: false })

        // Ahorros personales
        const { data: savingsData } = await supabase
          .from('savings')
          .select('*')
          .eq('user_id', currentUser.id)

        const savingsWithAmounts = await Promise.all(
          (savingsData || []).map(async (saving) => {
            const { data: txs } = await supabase
              .from('saving_transactions')
              .select('amount')
              .eq('saving_id', saving.id)
            const saved = (txs || []).reduce((acc, t) => acc + Number(t.amount || 0), 0)
            return {
              name: saving.name ? await decrypt(saving.name) : 'Sin nombre',
              target: Number(saving.target_amount || 0),
              saved,
              type: saving.type || 'saving',
              progress: saving.target_amount > 0 ? Math.round((saved / saving.target_amount) * 100) : 0,
            }
          })
        )

        // Ahorros compartidos
        const { data: sharedData } = await supabase
          .from('shared_savings')
          .select('*')
          .or(`creator_id.eq.${currentUser.id},partner_id.eq.${currentUser.id}`)
          .eq('status', 'accepted')

        const sharedWithAmounts = await Promise.all(
          (sharedData || []).map(async (saving) => {
            const { data: txs } = await supabase
              .from('shared_saving_transactions')
              .select('amount, type')
              .eq('shared_saving_id', saving.id)
            const saved = (txs || [])
              .filter(t => t.type === 'deposit')
              .reduce((acc, t) => acc + Number(t.amount || 0), 0)
            return {
              name: saving.name ? await decrypt(saving.name) : 'Sin nombre',
              target: Number(saving.target_amount || 0),
              saved,
              type: 'shared_saving',
              progress: saving.target_amount > 0 ? Math.round((saved / saving.target_amount) * 100) : 0,
            }
          })
        )

        // ── Construir contexto financiero ──────────────────────────────────
        const movs = movimientos || []
        const ingresos = movs.filter(m => m.type === 'income')
        const gastos   = movs.filter(m => m.type === 'expense')
        const totalIngresos = ingresos.reduce((a, m) => a + Number(m.amount), 0)
        const totalGastos   = gastos.reduce((a, m) => a + Number(m.amount), 0)
        const balance = totalIngresos - totalGastos

        // Agrupar gastos por categoría
        const gastosPorCategoria = gastos.reduce((acc, m) => {
          const cat = m.category || 'Sin categoría'
          acc[cat] = (acc[cat] || 0) + Number(m.amount)
          return acc
        }, {})
        const topGastos = Object.entries(gastosPorCategoria)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5)
          .map(([cat, amt]) => `  - ${cat}: $${amt.toLocaleString('es-CO')}`)
          .join('\n')

        const allSavings = [...savingsWithAmounts, ...sharedWithAmounts]

        const context = `
DATOS FINANCIEROS DEL USUARIO (últimos 3 meses):
Usuario: ${name}
Fecha de consulta: ${new Date().toLocaleDateString('es-CO', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}

RESUMEN GENERAL:
- Total ingresos (3 meses): $${totalIngresos.toLocaleString('es-CO')}
- Total gastos (3 meses): $${totalGastos.toLocaleString('es-CO')}
- Balance neto: $${balance.toLocaleString('es-CO')} (${balance >= 0 ? 'POSITIVO' : 'NEGATIVO'})
- Promedio ingresos/mes: $${Math.round(totalIngresos / 3).toLocaleString('es-CO')}
- Promedio gastos/mes: $${Math.round(totalGastos / 3).toLocaleString('es-CO')}
- Tasa de ahorro estimada: ${totalIngresos > 0 ? Math.round(((totalIngresos - totalGastos) / totalIngresos) * 100) : 0}%

TOP CATEGORÍAS DE GASTO:
${topGastos || '  Sin datos de categorías'}

METAS DE AHORRO Y DEUDAS (${allSavings.length} en total):
${allSavings.length === 0
  ? '  Sin metas creadas'
  : allSavings.map(s =>
    `  - "${s.name}" (${s.type === 'debt' ? 'DEUDA' : s.type === 'shared_saving' ? 'AHORRO COMPARTIDO' : 'AHORRO'}): $${s.saved.toLocaleString('es-CO')} de $${s.target.toLocaleString('es-CO')} (${s.progress}% completado)`
  ).join('\n')
}

ÚLTIMOS MOVIMIENTOS (máx. 10):
${movs.slice(0, 10).map(m =>
  `  - [${m.date}] ${m.type === 'income' ? 'INGRESO' : 'GASTO'} $${Number(m.amount).toLocaleString('es-CO')}${m.category ? ` (${m.category})` : ''}${m.description ? `: ${m.description}` : ''}`
).join('\n') || '  Sin movimientos recientes'}
`.trim()

        setFinancialContext(context)

        // Mensaje de bienvenida
        setMessages([{
          role: 'assistant',
          content: t('ia_bienvenida').replace('{name}', name),
        }])
      } catch (err) {
        console.error('Error cargando datos:', err)
      } finally {
        setLoading(false)
      }
    }
    loadUserData()
  }, [navigate, isOnline])

  // ── Enviar mensaje ─────────────────────────────────────────────────────────
  async function sendMessage(text) {
    if (!isOnline) return
    const question = (text || input).trim()
    if (!question || sending) return

    setInput('')
    setShowSuggestions(false)
    setSending(true)

    const userMsg   = { role: 'user',      content: question }
    const loadingMsg = { role: 'assistant', content: '', loading: true }
    setMessages(prev => [...prev, userMsg, loadingMsg])

    try {
      // Llamada a la Supabase Edge Function
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token

      const response = await fetch(
        'https://lrwjahmdpflelhargtet.supabase.co/functions/v1/chat-ia',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            question,
            financialContext,
          }),
        }
      )

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}))
        throw new Error(errData.error || `Error ${response.status}`)
      }

      const { answer } = await response.json()

      setMessages(prev => [
        ...prev.filter(m => !m.loading),
        { role: 'assistant', content: answer },
      ])
    } catch (err) {
      console.error('Error IA:', err)
      setMessages(prev => [
        ...prev.filter(m => !m.loading),
        {
          role: 'assistant',
          content: t('ia_error_procesar'),
        },
      ])
    } finally {
      setSending(false)
      inputRef.current?.focus()
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  // ── Pantalla sin conexión ────────────────────────────────────────────────
  if (!isOnline) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 20, background: 'var(--color-bg)', padding: '0 24px', textAlign: 'center' }}>
        <div style={{ width: 70, height: 70, borderRadius: 20, background: 'linear-gradient(135deg,#ef4444,#b91c1c)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32 }}>📡</div>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--color-text)', marginBottom: 6 }}>{t('ia_sin_conexion_titulo')}</div>
          <div style={{ fontSize: 13, color: 'var(--color-muted)', maxWidth: 280, lineHeight: 1.5 }}>{t('ia_sin_conexion_desc')}</div>
        </div>
        <button
          onClick={() => navigate('/home')}
          style={{ padding: '12px 22px', borderRadius: 14, border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text)', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}
        >
          {t('ia_volver_inicio')}
        </button>
      </div>
    )
  }

  // ── Loading screen ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 20, background: 'var(--color-bg)' }}>
        <div style={{ width: 70, height: 70, borderRadius: 20, background: 'linear-gradient(135deg,#22c55e,#16a34a)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, animation: 'pulse 1.5s infinite' }}>🤖</div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--color-text)', marginBottom: 6 }}>{t('ia_preparando_titulo')}</div>
          <div style={{ fontSize: 13, color: 'var(--color-muted)' }}>{t('ia_preparando_desc')}</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {[0,1,2].map(i => (
            <div key={i} style={{ width: 10, height: 10, borderRadius: '50%', background: '#22c55e', animation: `bounce 0.6s infinite ${i * 0.15}s` }}/>
          ))}
        </div>
        <IAStyles />
      </div>
    )
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-bg)', display: 'flex', flexDirection: 'column' }}>

      {/* HEADER */}
      <header style={{
        background: 'var(--color-surface)',
        borderBottom: '1px solid var(--color-border)',
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}>
        <button
          onClick={() => navigate('/home')}
          style={{ width: 36, height: 36, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.06)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: 'var(--color-text)', transition: '0.2s', flexShrink: 0 }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}
          onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
        >←</button>

        <div style={{ width: 40, height: 40, borderRadius: 14, background: 'linear-gradient(135deg,#22c55e,#16a34a)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, boxShadow: '0 4px 12px rgba(34,197,94,0.3)' }}>🤖</div>

        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>{t('ia_header_titulo')}</div>
          <div style={{ fontSize: 11, color: '#22c55e', marginTop: -1, fontWeight: 600 }}>{t('ia_header_estado')}</div>
        </div>

        {/* Indicador de datos cargados */}
        <div style={{ fontSize: 10, color: 'var(--color-muted)', textAlign: 'right', lineHeight: 1.5 }}>
          <div style={{ color: '#22c55e', fontWeight: 700, fontSize: 11 }}>{t('ia_datos_listos')}</div>
          <div>{t('ia_ultimos_3_meses')}</div>
        </div>
      </header>

      {/* ÁREA DE MENSAJES */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 16px', maxWidth: 640, width: '100%', margin: '0 auto', boxSizing: 'border-box', paddingBottom: 160 }}>

        {/* Chip informativo */}
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{ display: 'inline-block', background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 999, padding: '6px 14px', fontSize: 11, color: '#22c55e', fontWeight: 600 }}>
            {t('ia_privacidad_chip')}
          </div>
        </div>

        {messages.map((msg, i) => (
          <MessageBubble key={i} msg={msg} t={t} />
        ))}

        {/* Sugerencias rápidas */}
        {showSuggestions && messages.length <= 1 && !sending && (
          <div style={{ marginTop: 8, animation: 'fadeIn 0.4s ease' }}>
            <div style={{ fontSize: 12, color: 'var(--color-muted)', marginBottom: 10, textAlign: 'center', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8 }}>
              {t('ia_preguntas_sugeridas')}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
              {QUICK_SUGGESTIONS.map((s, i) => (
                <button
                  key={i}
                  onClick={() => sendMessage(s)}
                  style={{
                    background: 'var(--color-surface)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 999,
                    padding: '8px 14px',
                    fontSize: 12,
                    color: 'var(--color-text)',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    fontWeight: 500,
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#22c55e'; e.currentTarget.style.color = '#22c55e' }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--color-border)'; e.currentTarget.style.color = 'var(--color-text)' }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* INPUT */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'var(--color-bg)',
        borderTop: '1px solid var(--color-border)',
        padding: '12px 16px 20px',
        zIndex: 100,
      }}>
        <div style={{ maxWidth: 640, margin: '0 auto', display: 'flex', gap: 10, alignItems: 'flex-end' }}>
          <div style={{
            flex: 1,
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 20,
            padding: '10px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            transition: 'border-color 0.2s',
          }}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('ia_input_placeholder')}
              rows={1}
              disabled={sending}
              style={{
                flex: 1,
                background: 'none',
                border: 'none',
                outline: 'none',
                resize: 'none',
                fontSize: 14,
                color: 'var(--color-text)',
                fontFamily: 'inherit',
                lineHeight: 1.5,
                maxHeight: 100,
                overflowY: 'auto',
              }}
              onInput={e => {
                e.target.style.height = 'auto'
                e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px'
              }}
            />
          </div>

          <button
            onClick={() => sendMessage()}
            disabled={!input.trim() || sending}
            style={{
              width: 46, height: 46,
              borderRadius: '50%',
              border: 'none',
              background: !input.trim() || sending
                ? 'rgba(255,255,255,0.05)'
                : 'linear-gradient(135deg,#22c55e,#16a34a)',
              color: !input.trim() || sending ? 'var(--color-muted)' : 'white',
              cursor: !input.trim() || sending ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 18,
              transition: 'all 0.2s',
              flexShrink: 0,
              boxShadow: !input.trim() || sending ? 'none' : '0 4px 14px rgba(34,197,94,0.35)',
            }}
          >
            {sending ? '⏳' : '➤'}
          </button>
        </div>

        <div style={{ textAlign: 'center', marginTop: 8, fontSize: 10, color: 'var(--color-muted)' }}>
          {t('ia_footer_hint')}
        </div>
      </div>

      <IAStyles />
    </div>
  )
}

function IAStyles() {
  return (
    <style>{`
      @keyframes pulse    { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(1.08);opacity:0.85} }
      @keyframes bounce   { 0%,80%,100%{transform:translateY(0);opacity:0.5} 40%{transform:translateY(-8px);opacity:1} }
      @keyframes fadeIn   { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
      @keyframes bubbleIn { from{opacity:0;transform:scale(0.88) translateY(6px)} to{opacity:1;transform:scale(1) translateY(0)} }
      @keyframes dotBounce{ 0%,80%,100%{transform:translateY(0)} 40%{transform:translateY(-5px)} }
    `}</style>
  )
}